import { AutocompleteProvider } from "../../types/enums";

/**
 * Separates "this provider is broken" from "this provider found nothing" so the failover chain
 * only moves on for real faults. A no-result response is not an error.
 */
export class GeoSearchError extends Error {
    public readonly providerId: AutocompleteProvider;
    public readonly status: number | null;

    constructor(providerId: AutocompleteProvider, message: string, status: number | null = null) {
        super(message);
        this.name = "GeoSearchError";
        this.providerId = providerId;
        this.status = status;
    }

    /**
     * A key, quota or outage problem — the provider stays broken for the next call too.
     * A 404 or a malformed query is not, so it must not park a healthy provider.
     */
    public get isProviderUnhealthy(): boolean {
        if (this.status === null)
            return true;

        return this.status === 401
            || this.status === 403
            || this.status === 429
            || this.status >= 500;
    }
}

export function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === "AbortError";
}

export async function fetchJsonAsync<T>(
    providerId: AutocompleteProvider,
    url: string,
    init?: RequestInit
): Promise<T> {
    let response: Response;

    try {
        response = await fetch(url, init);
    }
    catch (error) {
        if (isAbortError(error))
            throw error;

        throw new GeoSearchError(providerId, `Request to ${url} failed`, null);
    }

    if (!response.ok)
        throw new GeoSearchError(providerId, `HTTP ${response.status} from ${url}`, response.status);

    return await response.json() as T;
}
